import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box, CircularProgress, CssBaseline, ThemeProvider, Typography,
} from '@mui/material';
import inventoryItemPublicService, { type PublicInventoryItem as PublicInventoryItemType } from '../../services/inventoryItemPublicService';
import { renderAndSanitizeQrLanding } from '../../utils/qrLandingTemplate';
import { previewTheme } from './PreviewMock';

const SERIF = '"Cormorant Garamond", "EB Garamond", Georgia, "Times New Roman", serif';

const PublicInventoryItem: React.FC = () => {
  // lotNumber is captured for forward-compatible URLs but intentionally unused
  // pending a future API change that may accept/return it.
  const { shortId, lotNumber: _lotNumber } = useParams<{ shortId: string; lotNumber?: string }>();
  const [item, setItem] = useState<PublicInventoryItemType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<'not-found' | 'error' | null>(null);

  useEffect(() => {
    if (!shortId) {
      setError('not-found');
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    inventoryItemPublicService.getByShortId(shortId)
      .then(d => setItem(d))
      .catch(err => {
        if (err?.response?.status === 404) setError('not-found');
        else setError('error');
      })
      .finally(() => setLoading(false));
  }, [shortId]);

  const wineDetails = item?.wine
    ? [item.wine.producer, item.wine.wineName, item.wine.displayVintage]
        .filter(Boolean)
        .join(' · ')
    : null;

  // When the venue has an active QR landing template, render it (safely) in
  // place of the default layout. Rendering escapes every variable and then runs
  // the result through DOMPurify, so a template can only contribute inert markup.
  const templateHtml =
    item && item.qrLandingTemplate?.isActive && item.qrLandingTemplate.html
      ? renderAndSanitizeQrLanding(item.qrLandingTemplate.html, item)
      : null;

  return (
    <ThemeProvider theme={previewTheme}>
      <CssBaseline />
      {loading && <CenteredState><CircularProgress size={32} /></CenteredState>}
      {!loading && error === 'not-found' && (
        <CenteredState>
          <Typography
            sx={{
              fontSize: '0.7rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'primary.main',
              mb: 1.5,
              fontWeight: 600,
            }}
          >
            Unavailable
          </Typography>
          <Typography
            sx={{
              fontFamily: SERIF,
              fontSize: '1.4rem',
              color: 'text.primary',
              textAlign: 'center',
              maxWidth: 360,
              lineHeight: 1.3,
            }}
          >
            This page isn't available right now.
          </Typography>
        </CenteredState>
      )}
      {!loading && error === 'error' && (
        <CenteredState>
          <Typography sx={{ color: 'text.secondary' }}>
            Couldn't load this page. Please try again.
          </Typography>
        </CenteredState>
      )}
      {!loading && !error && item && templateHtml && (
        <Box
          sx={{ minHeight: '100vh', backgroundColor: 'background.default', color: 'text.primary' }}
          dangerouslySetInnerHTML={{ __html: templateHtml }}
        />
      )}

      {!loading && !error && item && !templateHtml && (
        <CenteredState>
          <Box sx={{ textAlign: 'center', maxWidth: 400 }}>
            <Typography
              sx={{
                fontFamily: SERIF,
                fontSize: '1.4rem',
                color: 'text.primary',
                lineHeight: 1.3,
                mb: 1,
              }}
            >
              {item.displayName}
            </Typography>

            {wineDetails && (
              <Typography
                sx={{
                  fontFamily: SERIF,
                  fontSize: '1rem',
                  color: 'text.secondary',
                  mb: 0.5,
                }}
              >
                {wineDetails}
              </Typography>
            )}

            {item.wine?.region && (
              <Typography
                sx={{
                  fontSize: '0.8rem',
                  color: 'text.disabled',
                  mb: 1,
                }}
              >
                {item.wine.region}
              </Typography>
            )}

            {item.sizeValue != null && item.sizeUnit && (
              <Typography
                sx={{
                  fontSize: '0.85rem',
                  color: 'text.secondary',
                  mb: 1.5,
                }}
              >
                {item.sizeValue} {item.sizeUnit}
              </Typography>
            )}

            <Typography
              sx={{
                fontFamily: 'monospace',
                fontSize: '0.7rem',
                color: 'text.disabled',
                letterSpacing: '0.05em',
              }}
            >
              {item.shortId}
            </Typography>
          </Box>
        </CenteredState>
      )}
    </ThemeProvider>
  );
};

const CenteredState: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Box
    sx={{
      minHeight: '100vh',
      backgroundColor: 'background.default',
      color: 'text.primary',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      p: 4,
    }}
  >
    {children}
  </Box>
);

export default PublicInventoryItem;
